import type { PlayerId, RoomSnapshot } from '../../../shared/types';

interface GameOverModalProps {
  snapshot: RoomSnapshot;
  myPlayerId: PlayerId | null;
  isHost: boolean;
  onPlayAgain: () => void;
  onDismiss: () => void;
}

export default function GameOverModal({
  snapshot,
  myPlayerId,
  isHost,
  onPlayAgain,
  onDismiss,
}: GameOverModalProps) {
  const draw = snapshot.draw;
  const iWon = !draw && !!myPlayerId && snapshot.winnerId === myPlayerId;
  const winner = snapshot.players.find((p) => p.id === snapshot.winnerId) ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ttt-bg/85 backdrop-blur-sm px-4"
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
      aria-label="Game over"
    >
      <div
        className="ttt-pop ttt-glass rounded-3xl p-7 sm:p-8 w-full max-w-sm flex flex-col items-center gap-5 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ttt-trophy text-6xl leading-none select-none">
          {draw ? '🤝' : iWon ? '🏆' : '💪'}
        </div>

        <div>
          <h2 className="font-display font-bold text-2xl text-ttt-ink">
            {draw ? "It's a draw!" : iWon ? 'You win!' : `${winner?.name ?? 'Opponent'} wins`}
          </h2>
          <p className="text-ttt-muted text-sm mt-1">
            {draw
              ? 'So close — run it back?'
              : iWon
                ? 'Three in a row — well played!'
                : 'Better luck next round!'}
          </p>
        </div>

        {/* Session score */}
        <div className="w-full flex flex-col gap-2">
          {snapshot.players.map((p) => {
            const isWinner = !draw && snapshot.winnerId === p.id;
            return (
              <div
                key={p.id}
                className={[
                  'flex items-center gap-3 rounded-xl px-3.5 py-2.5 border',
                  isWinner
                    ? 'border-ttt-accent/60 bg-ttt-accent/10'
                    : 'border-white/10 bg-white/5',
                ].join(' ')}
              >
                <span className={`font-display font-bold ${p.piece === 'X' ? 'text-xpiece' : 'text-opiece'}`}>
                  {p.piece}
                </span>
                <span className="flex-1 truncate text-left text-ttt-ink font-medium">
                  {p.isBot && <span className="mr-1">🤖</span>}
                  {p.name}
                  {p.id === myPlayerId && <span className="text-ttt-muted text-sm"> (you)</span>}
                  {p.isBot && (
                    <span className="ml-1.5 rounded-full bg-white/10 px-1.5 py-px text-[10px] font-bold uppercase text-ttt-muted">
                      {p.botDifficulty ?? 'bot'}
                    </span>
                  )}
                </span>
                <span className="text-ttt-muted text-sm tabular-nums">★ {p.wins}</span>
              </div>
            );
          })}
        </div>

        {isHost ? (
          <button
            onClick={onPlayAgain}
            className="ttt-gradient w-full rounded-xl px-4 py-3.5 font-display font-semibold text-lg text-ttt-bg bg-gradient-to-r from-ttt-accent to-opiece hover:brightness-110 active:scale-[0.98] transition shadow-glow"
          >
            Play Again
          </button>
        ) : (
          <p className="text-ttt-muted text-sm">Waiting for host to start a rematch…</p>
        )}

        <button
          onClick={onDismiss}
          className="text-ttt-muted text-xs hover:text-ttt-ink transition-colors"
        >
          View final board
        </button>
      </div>
    </div>
  );
}
